/**
 * The sections of "Add to downline", top to bottom on one page.
 *
 * These were four wizard steps. Every field, cap, hint and explanatory sentence
 * is carried over verbatim; what is gone is the gating — you can see and answer
 * all of it at once, in any order. The one thing role still controls is
 * VISIBILITY: bet limits and game switches are member-level settings, so on an
 * agent the whole section is absent rather than shown greyed out. That was the
 * wizard's best idea and it survives the collapse intact.
 *
 * Final review lives in the sticky rail (not in the form column) so confirmation
 * and Create stay on screen while you fill the fields.
 */

import { useState, type ReactNode } from 'react'
import {
  Card,
  Chip,
  Field,
  Icon,
  Input,
  Legend,
  MoneyInput,
  RoleChip,
  Textarea,
  Toggle,
} from '@w7/ui'
import { Amount } from '@/app/bits'
import {
  ASSIGNABLE_CURRENCY_TYPES,
  canPickCurrency,
  convertDisplayAmount,
  displayToInrMajor,
  isSportsbookCurrencyLockedOnEdit,
  parseCurrencyType,
} from '@/lib/currency'
import { EMPTY, displayMoney, displayMoneyCompact, money, pct, sharePoints } from '@/lib/format'
import { GAME_LABEL, ROLE_LABEL, roleDepth, type GameSwitches, type Role } from '@/data/types'
import type { CreateParent } from '@/api/hooks/create/useCreateParent'
import { loginEmailFromUsername } from '@/api/map/create'
import {
  assignableCreditForRole,
  downlineMaxCeiling,
  downlineMaxFieldKey,
  downlineMaxRoles,
  parseNumber,
  parentMaxBet,
  casinoShareStep,
  minCasinoShareOf,
  minShareOf,
  myShareKeep,
  shareStep,
  USERNAME_FORMAT_HINT,
  type Availability,
  type CreateSummary,
  type CreateValues,
} from './summary'
import { isPasswordValid, passwordChecks } from './passwordPolicy'
import { PasswordInput } from './PasswordInput'

export const GAME_KEYS: (keyof GameSwitches)[] = [
  'casino',
  'cardGame',
  'liveGame',
  'sportsbook',
]

/* ---------------------------------------------------------------- the map -- */

export interface SectionMeta {
  id: string
  label: string
  /** The validated fields that belong to this section — drives the checklist state. */
  fields: string[]
  /** What the checklist and the anchor nav focus when they navigate here. */
  firstControl: string | null
  memberOnly?: boolean
}

/** One list, read by the page, the rail checklist and the anchor nav. */
export const SECTIONS: SectionMeta[] = [
  {
    id: 'role',
    label: 'Role & identity',
    fields: ['username', 'displayName', 'password', 'confirmPassword'],
    firstControl: 'role',
  },
  {
    id: 'credit',
    label: 'Credit',
    fields: ['creditLimit'],
    firstControl: 'creditLimit',
  },
  {
    id: 'rates',
    label: 'Rates & commission',
    fields: ['share', 'shareCasino', 'fancy', 'bookmaker', 'matchOdds'],
    firstControl: 'share',
  },
  {
    id: 'games',
    label: 'Games & bet limits',
    fields: ['minBet', 'maxBet'],
    firstControl: 'minBet',
    memberOnly: true,
  },
]

/** Rates fields for the checklist — INR members have no editable partnership share. */
export function ratesSectionFields(
  role: Role,
  currency: 'INR' | 'POINTS' | string = 'POINTS',
): string[] {
  const showShare = role !== 'player' || parseCurrencyType(currency) !== 'INR'
  if (showShare) return ['share', 'shareCasino', 'fancy', 'bookmaker', 'matchOdds']
  return ['fancy', 'bookmaker', 'matchOdds']
}

/** Credit-section field ids for the chosen role (own limit + downline maxes). */
export function creditSectionFields(role: Role): string[] {
  return ['creditLimit', ...downlineMaxRoles(role).map(downlineMaxFieldKey)]
}

/* ------------------------------------------------------------- split bars -- */

/** A proportion bar — "of this whole, this much is that". */
export function SplitBar({
  label,
  segments,
}: {
  label: string
  segments: { id: string; value: number; color: string }[]
}) {
  const total = segments.reduce((sum, seg) => sum + Math.max(0, seg.value), 0)
  return (
    <div className="w7-splitbar" role="img" aria-label={label}>
      {segments.map((seg) => {
        const width = total > 0 ? (Math.max(0, seg.value) / total) * 100 : 0
        return width > 0 ? (
          <span key={seg.id} style={{ width: `${width}%`, background: seg.color }} />
        ) : null
      })}
    </div>
  )
}

export const SEG_ALLOC = 'var(--w7-orange)'
export const SEG_REST = 'var(--w7-border-strong)'
/* Over the ceiling, matching how the kit's Meter flips above 100% — an
   over-allocation must look over, not merely full. */
export const SEG_OVER = 'var(--w7-loss)'
export const SEG_YOU = 'var(--w7-c1)'
export const SEG_THEM = 'var(--w7-c3)'

/* ------------------------------------------------------------------- ctx -- */

export interface SectionCtx {
  parent: CreateParent
  role: Role
  roles: Role[]
  values: CreateValues
  set: <K extends keyof CreateValues>(key: K, value: CreateValues[K]) => void
  /** Blur handler. Marks a field answered ONLY when it carries a value. */
  answer: (field: string, value: string) => void
  /** The error to SHOW — null until the field has been answered or Create pressed. */
  errorFor: (field: string) => string | null
  register: (field: string) => (el: HTMLElement | null) => void
  availability: Availability
  summary: CreateSummary
  /** Create (default) vs Edit Member — hides create-only chrome. */
  variant?: 'create' | 'edit'
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="w7-create-section" aria-labelledby={`${id}-h`} tabIndex={-1}>
      <h2 id={`${id}-h`} className="w7-create-h2">
        {title}
      </h2>
      {children}
    </section>
  )
}

/* ------------------------------------------------- 1 · role and identity -- */

export function RoleIdentitySection({ ctx }: { ctx: SectionCtx }) {
  const { parent, role, roles, values, set, answer, errorFor, register, availability } = ctx
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const trimmed = values.username.trim()
  return (
    <Section id="role" title="Role & identity">
      <Card
        title="Level"
        subtitle={`You are ${ROLE_LABEL[parent.role]}. You may create any level below you, including Member.`}
      >
        <div className="w7-optiongrid">
          {roles.map((option, index) => (
            <button
              key={option}
              type="button"
              className="w7-option"
              // Switching level hides the member-only section but keeps every
              // value typed into it — explore a different level and nothing you
              // wrote is destroyed. The form being replaced wiped it.
              ref={index === 0 ? register('role') : undefined}
              aria-pressed={option === role}
              onClick={() => set('role', option)}
            >
              <span className="w7-option-head">
                <RoleChip role={option} label={ROLE_LABEL[option]} />
                {option === role ? <Icon name="check" size={16} /> : null}
              </span>
              <span className="w7-option-note">
                {option === 'player'
                  ? 'Member (bottom of the ladder). Places bets; no downline of their own.'
                  : `Level ${roleDepth(option) + 1} on the ladder — sits directly under you.`}
              </span>
            </button>
          ))}
        </div>
        <p className="w7-card-sub" style={{ marginTop: 'var(--w7-s3)' }}>
          Company Master → Triple Master → Double Master → Super Master → Master → Member. As{' '}
          {ROLE_LABEL[parent.role]} you may create {roles.map((r) => ROLE_LABEL[r]).join(' or ')}.{' '}
          {ROLE_LABEL[role]} will sit directly under {parent.username}.
        </p>
      </Card>

      <Card title="Identity" subtitle="How this account is named, found, and will eventually sign in.">
        <div className="w7-grid w7-grid-2">
          <Field
            label="Username"
            required
            hint={USERNAME_FORMAT_HINT}
            error={errorFor('username')}
            aside={
              trimmed && availability !== 'idle' ? (
                <span className="w7-avail" data-state={availability}>
                  {availability === 'checking' ? (
                    'Checking'
                  ) : availability === 'free' ? (
                    <>
                      <Icon name="check" size={13} /> Available
                    </>
                  ) : (
                    <>
                      <Icon name="close" size={13} /> Taken
                    </>
                  )}
                </span>
              ) : null
            }
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                ref={register('username')}
                aria-describedby={describedBy}
                invalid={invalid}
                autoComplete="off"
                spellCheck={false}
                placeholder="e.g. rk.master01"
                value={values.username}
                onChange={(event) =>
                  set('username', event.target.value.replace(/\s/g, '').toLowerCase())
                }
                onBlur={(event) => answer('username', event.target.value)}
              />
            )}
          </Field>

          <Field
            label="Display name"
            required
            hint="Shown in tables and statements."
            error={errorFor('displayName')}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                ref={register('displayName')}
                aria-describedby={describedBy}
                invalid={invalid}
                autoComplete="off"
                placeholder={trimmed || 'e.g. Ravi Kumar'}
                value={values.displayName}
                onChange={(event) => set('displayName', event.target.value)}
                onBlur={(event) => answer('displayName', event.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="w7-grid w7-grid-2" style={{ marginTop: 'var(--w7-s4)' }}>
          <Field
            label="Password"
            required
            hint="They will use this password to sign in to the console. Keep it strong and share it securely."
            error={errorFor('password')}
          >
            {({ id, describedBy, invalid }) => {
              const passwordOk = isPasswordValid(values.password)
              const passwordLiveInvalid = Boolean(values.password) && !passwordOk
              return (
                <>
                  <PasswordInput
                    id={id}
                    ref={register('password')}
                    describedBy={describedBy}
                    invalid={invalid || passwordLiveInvalid}
                    valid={Boolean(values.password) && passwordOk}
                    autoComplete="new-password"
                    value={values.password}
                    onChange={(value) => set('password', value)}
                    onBlur={(event) => answer('password', event.target.value)}
                    visible={showPassword}
                    onToggleVisible={() => setShowPassword((v) => !v)}
                  />
                  {values.password ? (
                    <ul className="w7-create-pwchecks" aria-label="Password requirements">
                      {passwordChecks(values.password).map((check) => (
                        <li key={check.key} data-passed={check.passed ? 'true' : 'false'}>
                          <Icon name={check.passed ? 'check' : 'close'} size={12} />
                          {check.label}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              )
            }}
          </Field>

          <Field
            label="Confirm password"
            required
            error={errorFor('confirmPassword')}
          >
            {({ id, describedBy, invalid }) => (
              <PasswordInput
                id={id}
                ref={register('confirmPassword')}
                describedBy={describedBy}
                invalid={invalid}
                autoComplete="new-password"
                value={values.confirmPassword}
                onChange={(value) => set('confirmPassword', value)}
                onBlur={(event) => answer('confirmPassword', event.target.value)}
                visible={showConfirmPassword}
                onToggleVisible={() => setShowConfirmPassword((v) => !v)}
              />
            )}
          </Field>
        </div>

        <div style={{ marginTop: 'var(--w7-s4)' }}>
          <Field
            label="Comments"
            hint="Free text kept on the account — who introduced them, what was agreed."
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                placeholder="Optional"
                value={values.comments}
                onChange={(event) => set('comments', event.target.value)}
              />
            )}
          </Field>
        </div>
      </Card>
    </Section>
  )
}

/* ------------------------------------------------------------ 2 · credit -- */

export function CreditSection({ ctx }: { ctx: SectionCtx }) {
  const { parent, role, values, set, answer, errorFor, register, summary } = ctx
  const isMember = role === 'player'
  const maxRoles = downlineMaxRoles(role)
  const creditLimit = parseNumber(values.creditLimit)
  const childCurrency = parseCurrencyType(values.currencyType)
  const remaining = convertDisplayAmount(
    parent.availableCredit,
    parseCurrencyType(parent.currencyType),
    childCurrency,
  )
  // Parent availableCredit is already role-capped on create; keep assignableCreditForRole
  // so edit / other callers that pass raw remaining still respect parent downline max.
  const roleAssignable = assignableCreditForRole(remaining, role, parent.downlineMaxCredit)
  const remainingLabel = money(displayToInrMajor(roleAssignable, childCurrency), {
    currency: childCurrency,
  })
  const allocatingLabel = money(displayToInrMajor(creditLimit ?? 0, childCurrency), {
    currency: childCurrency,
  })

  return (
    <Section id="credit" title="Credit">
      <Card
        title="Credit limit"
        subtitle={
          isMember
            ? 'The most this member may ever hold. Bets are rejected once their exposure passes it.'
            : 'The ceiling for this agent and everything beneath it. They allocate their own downline out of this.'
        }
      >
        {/* The old form asked for five credit limits, one per level, and showed
            all five whatever role you had picked. Only the one that applies to
            the chosen level is here — plus, for agents, the cascading maxes they
            may hand to levels beneath them. */}
        {canPickCurrency(parent.role, role) ? (
          <>
          <Field
            label="Currency"
            required
            hint={
              ctx.variant === 'edit'
                ? 'Sportsbook currency is set at create (Lab). Casino Currency Flag below can still change.'
                : role === 'player'
                  ? 'INR or Points (1 point = ₹100). Direct CM member — not under a TM.'
                  : 'INR or Points (1 point = ₹100). Whole TM downline uses this.'
            }
          >
            {({ id }) => (
              <select
                id={id}
                className="w7-input"
                value={values.currencyType}
                disabled={isSportsbookCurrencyLockedOnEdit(ctx.variant)}
                onChange={(event) =>
                  set('currencyType', event.target.value as CreateValues['currencyType'])
                }
              >
                {ASSIGNABLE_CURRENCY_TYPES.map((c) => (
                  <option key={c} value={c}>
                    {c === 'POINTS' ? 'Points (1 = ₹100)' : 'INR (rupees)'}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field
            label="Casino Currency Flag"
            required
            hint="Evolution currency (HKD or AED). INR trees default HKD — balance to Evolution ÷10."
          >
            {({ id }) => (
              <select
                id={id}
                className="w7-input"
                value={values.casinoCurrencyType}
                onChange={(event) =>
                  set(
                    'casinoCurrencyType',
                    event.target.value === 'AED' ? 'AED' : 'HKD',
                  )
                }
              >
                <option value="HKD">HKD</option>
                <option value="AED">AED</option>
              </select>
            )}
          </Field>
          <Field
            label="Crash Games Currency"
            required
            hint="SoftGamings Fundist wallet currency (Lab fundistCurrencyType). Default HKD."
          >
            {({ id }) => (
              <select
                id={id}
                className="w7-input"
                value={values.fundistCurrencyType}
                onChange={(event) =>
                  set(
                    'fundistCurrencyType',
                    (['HKD', 'AED', 'USD', 'SGD', 'INR'].includes(event.target.value)
                      ? event.target.value
                      : 'HKD') as CreateValues['fundistCurrencyType'],
                  )
                }
              >
                <option value="HKD">HKD</option>
                <option value="AED">AED</option>
                <option value="USD">USD</option>
                <option value="SGD">SGD</option>
                <option value="INR">INR</option>
              </select>
            )}
          </Field>
          </>
        ) : null}
        <Field
          label={`Credit limit for this ${ROLE_LABEL[role]}`}
          required
          hint={`Up to ${remainingLabel} remaining credit — your line minus what you already assigned${
            parent.downlineMaxCredit?.[role] != null
              ? `, capped by your ${ROLE_LABEL[role]} max`
              : ''
          }. Setting it funds their opening balance; Give/Take adjusts cash later.`}
          error={errorFor('creditLimit')}
          aside={<Chip size="sm">Max {remainingLabel}</Chip>}
        >
          {({ id, describedBy, invalid }) => (
            <MoneyInput
              id={id}
              ref={register('creditLimit')}
              aria-describedby={describedBy}
              invalid={invalid}
              currency={values.currencyType}
              value={values.creditLimit}
              onChange={(event) => set('creditLimit', event.target.value)}
              onBlur={(event) => answer('creditLimit', event.target.value)}
            />
          )}
        </Field>

        <div style={{ marginTop: 'var(--w7-s5)' }}>
          <div className="w7-row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <span className="w7-micro">Available to assign</span>
            <span className="w7-mono">{remainingLabel}</span>
          </div>
          <SplitBar
            label={`Of your ${remainingLabel} remaining credit, ${allocatingLabel} is being assigned`}
            segments={[
              {
                id: 'alloc',
                value: summary.allocating,
                color: summary.overAllocated ? SEG_OVER : SEG_ALLOC,
              },
              { id: 'rest', value: summary.remaining, color: SEG_REST },
            ]}
          />
          <div style={{ marginTop: 'var(--w7-s3)' }}>
            <Legend
              items={[
                {
                  label: 'Assigning',
                  color: summary.overAllocated ? SEG_OVER : SEG_ALLOC,
                  // Nothing typed yet is not the same as a limit of zero.
                  value: money(summary.creditLimit === null ? null : summary.allocating, {
                    currency: childCurrency,
                    alreadyDisplay: true,
                  }),
                },
                {
                  label: 'Still available',
                  color: SEG_REST,
                  value: money(summary.remaining, { currency: childCurrency, alreadyDisplay: true }),
                },
              ]}
            />
          </div>
          <p className="w7-card-sub" style={{ marginTop: 'var(--w7-s3)' }}>
            Credit line{' '}
            {money(parent.creditLimit, {
              currency: parent.currencyType,
              alreadyDisplay: true,
            })}{' '}
            · ledger cash{' '}
            {money(parent.balance, { currency: parent.currencyType, alreadyDisplay: true })}.
            {ctx.variant === 'edit'
              ? ' Raising the limit expands what they may hold; Give/Take still moves cash.'
              : ' Create assigns from the line (child opens with balance = credit limit); Give/Take moves cash without changing the line.'}
          </p>
        </div>
      </Card>

      {maxRoles.length > 0 && ctx.variant !== 'edit' ? (
        <Card
          title="Downline max credit"
          subtitle="Ceilings this agent may hand to each level beneath them. Each rung is capped by the one above and by your own max for that level."
        >
          <div className="w7-grid w7-grid-2">
            {maxRoles.map((level) => {
              const field = downlineMaxFieldKey(level)
              const ceiling = downlineMaxCeiling(
                level,
                maxRoles,
                creditLimit,
                values.downlineMax,
                parent.downlineMaxCredit,
              )
              return (
                <Field
                  key={level}
                  label={`${ROLE_LABEL[level]} max credit`}
                  required
                  hint={
                    ceiling === null
                      ? 'Set this account’s credit limit (and the levels above) first.'
                      : `At most ${displayMoney(ceiling, values.currencyType)}.`
                  }
                  error={errorFor(field)}
                  aside={
                    ceiling !== null ? (
                      <Chip size="sm">Max {displayMoneyCompact(ceiling, values.currencyType)}</Chip>
                    ) : undefined
                  }
                >
                  {({ id, describedBy, invalid }) => (
                    <MoneyInput
                      id={id}
                      ref={register(field)}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      currency={values.currencyType}
                      value={values.downlineMax[level] ?? ''}
                      onChange={(event) =>
                        set('downlineMax', { ...values.downlineMax, [level]: event.target.value })
                      }
                      onBlur={(event) => answer(field, event.target.value)}
                    />
                  )}
                </Field>
              )
            })}
          </div>
        </Card>
      ) : null}
    </Section>
  )
}

/* ------------------------------------------------------------- 3 · rates -- */

export function RatesSection({ ctx }: { ctx: SectionCtx }) {
  const { parent, role, values, set, answer, errorFor, register, summary } = ctx
  const isMember = role === 'player'
  const ccy = parseCurrencyType(values.currencyType ?? parent.currencyType)
  const isInr = ccy === 'INR'
  // INR members: SportsBook forced to 100%. POINTS members: Master enter My Share.
  const showShareForm = !isMember || !isInr
  const shareLabel = isInr ? 'Member Rate' : 'My share'
  const shareCasinoLabel = isInr ? 'Member Rate (Casino)' : 'My share (Casino)'
  const shareInc = shareStep(ccy)
  const casinoInc = casinoShareStep(ccy)
  const {
    yourShare,
    theirShare,
    yourGap,
    restOfChain,
    maxShare,
    theirCasinoShare,
    yourCasinoGap,
    restOfCasinoChain,
    maxCasinoShare,
  } = summary
  const maxCommission = parent.commission
  const dp = isInr ? 2 : 0
  const minShareSb = minShareOf(yourShare, ccy)
  const minShareCasino = minCasinoShareOf(ccy)
  const casinoSliderMax = maxCasinoShare ?? Math.max(100, theirCasinoShare, minShareCasino)
  const sbSliderMax = maxShare ?? Math.max(100, theirShare, minShareSb)

  const commissionField = (
    field: 'fancy' | 'bookmaker' | 'matchOdds',
    label: string,
    max: number,
  ) => (
    <Field label={label} hint={`Up to ${pct(max, 2)}.`} error={errorFor(field)}>
      {({ id, describedBy, invalid }) => (
        <Input
          id={id}
          ref={register(field)}
          aria-describedby={describedBy}
          invalid={invalid}
          numeric
          suffix="%"
          value={values[field]}
          onChange={(event) => set(field, event.target.value)}
          onBlur={(event) => answer(field, event.target.value)}
        />
      )}
    </Field>
  )

  const shareField = (
    field: 'share' | 'shareCasino',
    label: string,
    floor: number,
    value: string,
    sliderValue: number,
    sliderMax: number,
    ariaLabel: string,
    opts?: { step?: number; hint?: string; maxAttr?: number },
  ) => {
    const step = opts?.step ?? shareInc
    const keep = myShareKeep(sliderValue, floor, ccy)
    const hint =
      opts?.hint ??
      (isInr
        ? `My Share: ${sharePoints(keep, dp)} · (Min: ${sharePoints(floor, dp)}) (Max: ${sharePoints(sliderMax, dp)})`
        : `Own keep of pot · (Max: ${sharePoints(sliderMax, dp)})`)
    return (
      <div className="w7-grid w7-grid-2">
        <Field label={label} required hint={hint} error={errorFor(field)}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              ref={register(field)}
              aria-describedby={describedBy}
              invalid={invalid}
              numeric
              step={step}
              min={floor}
              max={opts?.maxAttr}
              value={value}
              onChange={(event) => set(field, event.target.value)}
              onBlur={(event) => answer(field, event.target.value)}
            />
          )}
        </Field>
        <div className="w7-field">
          <span className="w7-field-label">Drag to set</span>
          <span className="w7-row" style={{ height: 38 }}>
            <input
              className="w7-range"
              type="range"
              min={floor}
              max={Math.max(floor, sliderMax)}
              step={step}
              value={Math.min(Math.max(floor, sliderValue), sliderMax)}
              aria-label={ariaLabel}
              onChange={(event) => {
                set(field, event.target.value)
                answer(field, event.target.value)
              }}
            />
          </span>
        </div>
      </div>
    )
  }

  return (
    <Section id="rates" title="Rates & commission">
      {!showShareForm ? (
        <Card
          title="Partnership share"
          subtitle="INR members: SportsBook 100% (rate 1)."
        >
          <p className="w7-dim" style={{ margin: 0 }}>
            Hierarchy forces sportsbook share_bp to 10000 for INR members. Casino leaf 100.
          </p>
        </Card>
      ) : (
        <Card
          title="Partnership share"
          subtitle={
            isInr
              ? 'SportsBook: Member Rate 0–1 (≥ yours). Casino: Member Rate 0–100.'
              : 'POINTS SportsBook: My share any integer % (parent keep on child). Casino: any numeric.'
          }
        >
          {shareField(
            'share',
            shareLabel,
            minShareSb,
            values.share,
            theirShare,
            sbSliderMax,
            `${shareLabel}, sportsbook`,
            {
              maxAttr: maxShare ?? undefined,
              hint: isInr
                ? undefined
                : 'Own keep % of pot (any integer ≥ 0)',
            },
          )}

          <div style={{ marginTop: 'var(--w7-s5)' }}>
            {isInr ? (
              <>
                <SplitBar
                  label={`Member Rate ${sharePoints(theirShare, dp)} · My Share ${sharePoints(yourGap, dp)}`}
                  segments={[
                    { id: 'you', value: yourGap, color: SEG_YOU },
                    { id: 'them', value: theirShare, color: SEG_THEM },
                    { id: 'rest', value: restOfChain, color: SEG_REST },
                  ]}
                />
                <div style={{ marginTop: 'var(--w7-s3)' }}>
                  <Legend
                    items={[
                      { label: 'My Share', color: SEG_YOU, value: sharePoints(yourGap, dp) },
                      { label: 'Member Rate', color: SEG_THEM, value: sharePoints(theirShare, dp) },
                      {
                        label: 'Rest to leaf',
                        color: SEG_REST,
                        value: sharePoints(restOfChain, dp),
                      },
                    ]}
                  />
                </div>
              </>
            ) : (
              <>
                <SplitBar
                  label={`My share ${sharePoints(theirShare, dp)} · Rest ${sharePoints(restOfChain, dp)}`}
                  segments={[
                    { id: 'keep', value: theirShare, color: SEG_THEM },
                    { id: 'rest', value: restOfChain, color: SEG_REST },
                  ]}
                />
                <div style={{ marginTop: 'var(--w7-s3)' }}>
                  <Legend
                    items={[
                      { label: 'My share', color: SEG_THEM, value: sharePoints(theirShare, dp) },
                      {
                        label: 'Rest of pot',
                        color: SEG_REST,
                        value: sharePoints(restOfChain, dp),
                      },
                    ]}
                  />
                </div>
              </>
            )}
          </div>

          <div style={{ marginTop: 'var(--w7-s6)' }}>
            {shareField(
              'shareCasino',
              shareCasinoLabel,
              minShareCasino,
              values.shareCasino,
              theirCasinoShare,
              casinoSliderMax,
              `${shareCasinoLabel}, casino`,
              {
                step: casinoInc,
                hint: isInr ? '(Min: 0) (Max: 100)' : 'Point currency — any numeric value',
                maxAttr: maxCasinoShare ?? undefined,
              },
            )}
          </div>

          <div style={{ marginTop: 'var(--w7-s5)' }}>
            {isInr ? (
              <>
                <SplitBar
                  label={`Casino Member Rate ${sharePoints(theirCasinoShare, dp)} · My Share ${sharePoints(yourCasinoGap, dp)}`}
                  segments={[
                    { id: 'you', value: yourCasinoGap, color: SEG_YOU },
                    { id: 'them', value: theirCasinoShare, color: SEG_THEM },
                    { id: 'rest', value: restOfCasinoChain, color: SEG_REST },
                  ]}
                />
                <div style={{ marginTop: 'var(--w7-s3)' }}>
                  <Legend
                    items={[
                      {
                        label: 'My Share',
                        color: SEG_YOU,
                        value: sharePoints(yourCasinoGap, dp),
                      },
                      {
                        label: 'Member Rate',
                        color: SEG_THEM,
                        value: sharePoints(theirCasinoShare, dp),
                      },
                      {
                        label: 'Rest to leaf',
                        color: SEG_REST,
                        value: sharePoints(restOfCasinoChain, dp),
                      },
                    ]}
                  />
                </div>
              </>
            ) : (
              <>
                <SplitBar
                  label={`Casino My share ${sharePoints(theirCasinoShare, dp)} · Rest ${sharePoints(restOfCasinoChain, dp)}`}
                  segments={[
                    { id: 'keep', value: theirCasinoShare, color: SEG_THEM },
                    { id: 'rest', value: restOfCasinoChain, color: SEG_REST },
                  ]}
                />
                <div style={{ marginTop: 'var(--w7-s3)' }}>
                  <Legend
                    items={[
                      {
                        label: 'My share',
                        color: SEG_THEM,
                        value: sharePoints(theirCasinoShare, dp),
                      },
                      {
                        label: 'Rest of pot',
                        color: SEG_REST,
                        value: sharePoints(restOfCasinoChain, dp),
                      },
                    ]}
                  />
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      <Card
        title="Commission"
        subtitle="Paid down to this account on settled turnover. Each rate is capped at your own."
      >
        <div className="w7-grid w7-grid-3">
          {commissionField('fancy', 'Fancy', maxCommission.fancy)}
          {commissionField('bookmaker', 'Bookmaker', maxCommission.bookmaker)}
          {commissionField('matchOdds', 'Match odds', maxCommission.matchOdds)}
        </div>
      </Card>
    </Section>
  )
}

/* ------------------------------------------ 4 · games and bet limits (member) */

export function GamesSection({ ctx }: { ctx: SectionCtx }) {
  const { parent, values, set, answer, errorFor, register } = ctx
  const ceiling = parentMaxBet(parent)

  return (
    <Section id="games" title="Games & bet limits">
      <Card title="Bet limits" subtitle="The stake range allowed on a single bet.">
        <div className="w7-grid w7-grid-2">
          <Field
            label="Minimum bet"
            required
            hint="Stakes below this are rejected."
            error={errorFor('minBet')}
          >
            {({ id, describedBy, invalid }) => (
              <MoneyInput
                id={id}
                ref={register('minBet')}
                aria-describedby={describedBy}
                invalid={invalid}
                currency={values.currencyType}
                value={values.minBet}
                onChange={(event) => set('minBet', event.target.value)}
                onBlur={(event) => answer('minBet', event.target.value)}
              />
            )}
          </Field>
          <Field
            label="Maximum bet"
            required
            hint={
              ceiling === null
                ? 'Stakes above this are rejected.'
                : `Cannot exceed your own maximum of ${displayMoney(ceiling, values.currencyType, { round: true })}.`
            }
            error={errorFor('maxBet')}
          >
            {({ id, describedBy, invalid }) => (
              <MoneyInput
                id={id}
                ref={register('maxBet')}
                aria-describedby={describedBy}
                invalid={invalid}
                currency={values.currencyType}
                value={values.maxBet}
                onChange={(event) => set('maxBet', event.target.value)}
                onBlur={(event) => answer('maxBet', event.target.value)}
              />
            )}
          </Field>
        </div>
      </Card>

      <Card
        title="Games"
        subtitle="What this member may play. Everything off means they can hold credit but place nothing."
        flush
      >
        <ul className="w7-list">
          {GAME_KEYS.map((key) => (
            <li key={key}>
              <span className="w7-list-row">
                <span>{GAME_LABEL[key]}</span>
                <Toggle
                  checked={values.games[key]}
                  label={GAME_LABEL[key]}
                  onChange={(next) => set('games', { ...values.games, [key]: next })}
                />
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </Section>
  )
}

/* ------------------------------------------------------------ 5 · review -- */

export function ReviewSection({
  ctx,
  banner,
  ready = false,
}: {
  ctx: SectionCtx
  banner?: ReactNode
  /** True when every required field above is valid — drives the hero status. */
  ready?: boolean
}) {
  const { parent, role, values, summary } = ctx
  const isEdit = ctx.variant === 'edit'
  const isMember = role === 'player'
  const ccy = parseCurrencyType(values.currencyType)
  const isInr = ccy === 'INR'
  const showShareForm = !isMember || !isInr
  const trimmed = values.username.trim()
  const display = values.displayName.trim() || trimmed
  const minValue = parseNumber(values.minBet)
  const maxValue = parseNumber(values.maxBet)
  const gamesOn = GAME_KEYS.filter((key) => values.games[key]).map((key) => GAME_LABEL[key])
  const shareDp = isInr ? 2 : 0

  return (
    <div className="w7-create-review-host">
      {banner}
      <div
        className="w7-create-review"
        data-ready={ready ? 'true' : 'false'}
        aria-label="Confirmation of what will be recorded"
      >
        <header className="w7-create-review-hero">
          <div className="w7-create-review-status">
            <span className="w7-create-review-status-icon" aria-hidden>
              <Icon name={ready ? 'check' : 'warning'} size={18} />
            </span>
            <div>
              <p className="w7-create-review-kicker">Final check</p>
              <h3 className="w7-create-review-status-title">
                {ready
                  ? isEdit
                    ? 'Ready to save'
                    : 'Ready to create'
                  : 'Almost there — finish the items above'}
              </h3>
              <p className="w7-create-review-status-copy">
                {ready
                  ? isEdit
                    ? 'Changes apply when you save — review the panels below.'
                    : 'Hierarchy record only — create when it looks right.'
                  : 'Finish the fields on the left, then this flips to ready.'}
              </p>
            </div>
          </div>
          <div className="w7-create-review-identity">
            <RoleChip role={role} label={ROLE_LABEL[role]} />
            <div className="w7-create-review-identity-text">
              <strong className="w7-create-review-username">{trimmed || 'Username pending'}</strong>
              <span className="w7-create-review-under">
                under {parent.username} · {ROLE_LABEL[parent.role]}
              </span>
            </div>
          </div>
        </header>

        <div className="w7-create-review-grid">
          <article className="w7-create-review-panel">
            <h4 className="w7-create-review-panel-title">
              <Icon name="user" size={14} />
              Identity
            </h4>
            <dl className="w7-create-review-facts">
              <div>
                <dt>Level</dt>
                <dd>{ROLE_LABEL[role]}</dd>
              </div>
              <div>
                <dt>Username</dt>
                <dd className="w7-mono">{trimmed || EMPTY}</dd>
              </div>
              <div>
                <dt>Display name</dt>
                <dd>{display || EMPTY}</dd>
              </div>
              <div>
                <dt>Password</dt>
                <dd>
                  {values.password ? (
                    <Chip size="sm" tone="info">
                      Console login enabled
                    </Chip>
                  ) : (
                    EMPTY
                  )}
                </dd>
              </div>
              <div>
                <dt>Sign-in</dt>
                <dd>
                  {values.password ? (
                    <Chip size="sm" tone="info">
                      {values.username.trim()
                        ? loginEmailFromUsername(values.username)
                        : 'Set after username'}
                    </Chip>
                  ) : (
                    <Chip size="sm" tone="warn">
                      Needs password
                    </Chip>
                  )}
                </dd>
              </div>
            </dl>
          </article>

          <article className="w7-create-review-panel w7-create-review-panel-accent">
            <h4 className="w7-create-review-panel-title">
              <Icon name="wallet" size={14} />
              Credit &amp; share
            </h4>
            <div className="w7-create-review-kpis">
              <div className="w7-create-review-kpi">
                <span className="w7-create-review-kpi-label">Credit limit</span>
                <strong className="w7-create-review-kpi-value">
                  <Amount value={summary.creditLimit} currency={values.currencyType} alreadyDisplay />
                </strong>
              </div>
              <div className="w7-create-review-kpi">
                <span className="w7-create-review-kpi-label">Left with you</span>
                <strong
                  className="w7-create-review-kpi-value"
                  data-tone={summary.overAllocated ? 'loss' : undefined}
                >
                  <Amount value={summary.remaining} currency={values.currencyType} alreadyDisplay />
                </strong>
              </div>
            </div>
            {!showShareForm ? (
              <p className="w7-create-review-split-copy">
                INR members are stored at leaf Member Rate 1 (sportsbook and casino) — Lab
                fall-down.
              </p>
            ) : isInr ? (
              <>
                <SplitBar
                  label={`Sportsbook — My Share ${sharePoints(summary.yourGap, shareDp)}, Member Rate ${sharePoints(summary.theirShare, shareDp)}`}
                  segments={[
                    { id: 'you', value: summary.yourGap, color: SEG_YOU },
                    { id: 'them', value: summary.theirShare, color: SEG_THEM },
                    { id: 'rest', value: summary.restOfChain, color: SEG_REST },
                  ]}
                />
                <p className="w7-create-review-split-copy">
                  Sportsbook · My Share {sharePoints(summary.yourGap, shareDp)} · Member Rate{' '}
                  {sharePoints(summary.theirShare, shareDp)} · Rest{' '}
                  {sharePoints(summary.restOfChain, shareDp)}
                </p>
                <SplitBar
                  label={`Casino — My Share ${sharePoints(summary.yourCasinoGap, shareDp)}, Member Rate ${sharePoints(summary.theirCasinoShare, shareDp)}`}
                  segments={[
                    { id: 'you', value: summary.yourCasinoGap, color: SEG_YOU },
                    { id: 'them', value: summary.theirCasinoShare, color: SEG_THEM },
                    { id: 'rest', value: summary.restOfCasinoChain, color: SEG_REST },
                  ]}
                />
                <p className="w7-create-review-split-copy">
                  Casino · My Share {sharePoints(summary.yourCasinoGap, shareDp)} · Member Rate{' '}
                  {sharePoints(summary.theirCasinoShare, shareDp)} · Rest{' '}
                  {sharePoints(summary.restOfCasinoChain, shareDp)}
                </p>
              </>
            ) : (
              <>
                <SplitBar
                  label={`Sportsbook — My share ${sharePoints(summary.theirShare, shareDp)} · Rest ${sharePoints(summary.restOfChain, shareDp)}`}
                  segments={[
                    { id: 'keep', value: summary.theirShare, color: SEG_THEM },
                    { id: 'rest', value: summary.restOfChain, color: SEG_REST },
                  ]}
                />
                <p className="w7-create-review-split-copy">
                  Sportsbook · My share {sharePoints(summary.theirShare, shareDp)} · Rest{' '}
                  {sharePoints(summary.restOfChain, shareDp)}
                </p>
                <SplitBar
                  label={`Casino — My share ${sharePoints(summary.theirCasinoShare, shareDp)} · Rest ${sharePoints(summary.restOfCasinoChain, shareDp)}`}
                  segments={[
                    { id: 'keep', value: summary.theirCasinoShare, color: SEG_THEM },
                    { id: 'rest', value: summary.restOfCasinoChain, color: SEG_REST },
                  ]}
                />
                <p className="w7-create-review-split-copy">
                  Casino · My share {sharePoints(summary.theirCasinoShare, shareDp)} · Rest{' '}
                  {sharePoints(summary.restOfCasinoChain, shareDp)}
                </p>
              </>
            )}
            <dl className="w7-create-review-facts w7-create-review-facts-compact">
              <div>
                <dt>Commission</dt>
                <dd>
                  fancy {pct(parseNumber(values.fancy), 2)} · BM {pct(parseNumber(values.bookmaker), 2)}{' '}
                  · MO {pct(parseNumber(values.matchOdds), 2)}
                </dd>
              </div>
              {!isMember && ctx.variant !== 'edit'
                ? downlineMaxRoles(role).map((level) => (
                    <div key={level}>
                      <dt>{ROLE_LABEL[level]} max</dt>
                      <dd>
                        <Amount
                          value={parseNumber(values.downlineMax[level] ?? '')}
                          currency={values.currencyType}
                          alreadyDisplay
                        />
                      </dd>
                    </div>
                  ))
                : null}
            </dl>
          </article>

          <article className="w7-create-review-panel">
            <h4 className="w7-create-review-panel-title">
              <Icon name="settings" size={14} />
              {isMember ? 'Member settings' : 'Agent note'}
            </h4>
            {isMember ? (
              <>
                <dl className="w7-create-review-facts">
                  <div>
                    <dt>Bet limits</dt>
                    <dd>
                      <Amount value={minValue} currency={values.currencyType} alreadyDisplay /> –{' '}
                      <Amount value={maxValue} currency={values.currencyType} alreadyDisplay />
                    </dd>
                  </div>
                </dl>
                <div className="w7-create-review-games">
                  {gamesOn.length > 0 ? (
                    gamesOn.map((label) => (
                      <Chip key={label} size="sm" tone="info">
                        {label}
                      </Chip>
                    ))
                  ) : (
                    <span className="w7-dim">No games enabled</span>
                  )}
                </div>
              </>
            ) : (
              <p className="w7-create-review-note">
                Bet limits and game switches are set per member, not on this agent. You can configure
                those when you create members underneath.
              </p>
            )}
            <dl className="w7-create-review-facts w7-create-review-facts-compact">
              <div>
                <dt>Comments</dt>
                <dd>{values.comments.trim() || EMPTY}</dd>
              </div>
            </dl>
          </article>
        </div>
      </div>
    </div>
  )
}
